import { 
    setSession, 
    bookingRequest,
    fetchAvailableSubUnits,     
} from "../api/api.js"
import {
    getClientData,
    getBookingState,
    resetCostState,
    setBookingStateProp, 
    setBookingState, 
    setCostState,
    patchUnitDataById,
    deleteBookingAtIndex,
    getBookingStateArray,
    getUnitData,
    getSiteNameForUnit,
    initNewBooking,
    getCurrentStageId,
    setClientDataProp
} from "../state.js"
import { dom, elById, qs, qsAll } from "../utils/dom.js";
import { 
    showDropdownOptsInArray, 
    resetDropdown, 
    setDropdown,
    setDropdownMessage
} from "../ui/dropdown.js";
import * as UI from "../ui/ui.js";


import {
    getFirstAvSubUnitFromArr,
    getAvailablePitchTypes,
    matchMultiParentUnitIds,
    matchSingleParentUnitId,
    shouldShowPitchTypeFilter
} from "../utils/filters.js";
import { filterElementsByParentId, showOptionsWithParentId, toggle } from "../utils/ui-helpers.js";
import { buildUnitAddressString, isEmpty } from "../utils/general-helpers.js";

import { updateSummary, updateSummarySubUnitUI } from "../ui/checkout-summary.js";
import { renderBookingAccordion } from "../ui/multi-booking/summary-multi-booking-accordion.js";
import { returnToStageUI } from "../ui/stage-transition.js";
import { initialisePartyMemberSteppers } from "../ui/party-members.js";
import { resetExtrasUI, setExtrasUI } from "../ui/extras.js";
import { createBookingDisplayArray, isUsingFullPaymentOnly, transformCostData } from "../data-to-ui-adapters/data-transform-functions.js";
import { createAction } from "./action-creator.js";
import { runActionPhase } from './action-functions.js';

import { calendar } from "../checkout-visual-calendar.js";
import { AvailabilityType } from "../enums/availability-type.js";
import { toIsoDateString } from "../utils/date-utils.js";
import { buildInitialPartyMembersForUnit } from "../utils/party-member-utils.js";
import { buildSubUnitSelectionRequest } from "../utils/sub-unit-selection.js";

const DEFAULT_PARTY_SIZE = 1;

function getDefaultPartySize(stageOneFieldConfig = null) {
    const stageOneFields = stageOneFieldConfig || getClientData().uiData.stage_1_field_config || [];
    const partySizeField = stageOneFields.find(field => field.id === 'party-size');

    return parseIntb10(partySizeField?.default_value) || DEFAULT_PARTY_SIZE;
}

function shouldUsePartySizeDropdown(partyMembersForUnit, availabilityType) {
    return availabilityType !== AvailabilityType.Occupancy && Object.keys(partyMembersForUnit).length == 0;
}

function getPartyMemberQtyTotal(partyMembersForUnit) {
    return Object.values(partyMembersForUnit).reduce((total, partyMember) => total + parseIntb10(partyMember.qty), 0);
}

function getInitialPartySize({
    availabilityType,
    usePartySizeDropdown,
    partyMembersForUnit,
    stageOneFieldConfig = null,
    presetPartySize = null
}) {
    if (availabilityType === AvailabilityType.Occupancy) {
        return presetPartySize === null
            ? 0
            : parseIntb10(presetPartySize) || DEFAULT_PARTY_SIZE;
    }

    if (usePartySizeDropdown) return getDefaultPartySize(stageOneFieldConfig);

    return getPartyMemberQtyTotal(partyMembersForUnit) || DEFAULT_PARTY_SIZE;
}


export function addAction(actionName, handlers) {   
    actions[actionName] = createAction(actionName, handlers);
}

export const actions = {
    site: {         
        fetch: null, 
        set: (siteId) => { 
            setBookingStateProp('site', siteId);

            // Reset properties
            setBookingState(resetMap.onSiteChange);   
            resetCostState();
        },
        render: () =>UI.updateOnSiteChange(getBookingState())        
    },
    category: {         
        fetch: setSession,
        set: (categoryId, data) => {                   
            setBookingStateProp('category', categoryId);

            const siteGatewayId = parseIntb10(data?.payment_gateway?.gateway_id);
            if (siteGatewayId > 0)
                setClientDataProp('payment_gateway_id', siteGatewayId);

            // Reset properties
            setBookingState(resetMap.onCategoryChange);
            setCostState(data.booking_info);
        },
        render: () =>UI.updateOnCategoryChange(getBookingState())        
    },
    unit: {         
        fetch: setSession,
        set: (unitId, data = {}) => {
            // Build the party member quantities for this unit, including every mandatory minimum.
            const partyMembersForUnit = buildInitialPartyMembersForUnit(
                getClientData().party_members,
                unitId
            );

            // Reset any fields that may have been set after the unit dropdown
            setBookingState(resetMap.onUnitChange);
            
            // get the unit's location string
            const { 
                state, 
                city, 
                hide_sub_units, 
                single_plot, 
                restrict_sub_units, 
                occupancy 
            } = data.unit;

            // Unfortunately, unit data objects are incomplete when initially passed from smarty to JS
            // so patch the unit object in memory with the extended object passed from session when unit is set
            patchUnitDataById(
                data.unit.id, 
                {
                    availabilityType: parseIntb10(data.unit.availability_type),
                    address         : buildUnitAddressString(city, state),
                    thumbnail       : getUnitThumbnailSrcById(data.unit.id),
                    latitude        : data.unit.latitude,
                    longitude       : data.unit.longitude
                }
            );

            const availabilityType      = parseIntb10(data.unit.availability_type);
            const usePartySizeDropdown = shouldUsePartySizeDropdown(partyMembersForUnit, availabilityType);
            const initialPartySize      = getInitialPartySize({
                availabilityType,
                usePartySizeDropdown,
                partyMembersForUnit
            });

            // TODO: refactor this into a nested "unit" object?
            setBookingState({
                availability_type : availabilityType,
                unit              : parseIntb10(unitId),
                using_party_size  : usePartySizeDropdown,
                is_single_unit    : parseIntb10(single_plot),
                hide_sub_units    : parseIntb10(hide_sub_units),
                restrict_sub_units: parseIntb10(restrict_sub_units),
                party_members     : partyMembersForUnit,
                party_size        : initialPartySize,
                occupancy         : parseIntb10(occupancy),
                referrals         : data.referrals || []
            });

            document.dispatchEvent(new CustomEvent("CheckoutUnitChanged"));
            // Pass the consolidated costs/booking info data to state
            setCostState(data.booking_info || {});
        },
        render: (data) => {
            const bookingData = getBookingState();
            UI.updateOnUnitChange(
                bookingData,
                getUnitData(bookingData.unit),
                data?.referrals || []
            );
        }
    },
    party_size2: {
        fetch: setSession,
        set: (value, data) => {
            debugger
            setBookingStateProp('party_size', parseIntb10(value));
        },
        render: () => {
            const bookingData = getBookingState();
           UI.updateOnOccupancyPartySizeChange(bookingData);
        }
    },
    period: {
        fetch: async(actionName, data) => { 
            const { 
                unit, 
                category,
                is_single_unit 
            } = getBookingState();

            const periodResponseData = await setSession(actionName, data);
            const {
                arrival_date_unformatted: arrivalDateYmd,
                departure_date_unformatted: departureDateYmd
            } = periodResponseData;
            
            let availableSubUnits = null;
            const clientId = getClientData().config.client_id;

            // if the unit has sub-units, make a call for available sub-units to the back end
            // TODO: this should probably be moved out of period fetch, this is from early on in the refactor
            if (!is_single_unit) {                
                availableSubUnits = await fetchAvailableSubUnits(
                    arrivalDateYmd, 
                    departureDateYmd, 
                    unit, 
                    category, 
                    clientId
                );                
            }

            // include the period in the response object
            periodResponseData.period = data.period;
            const responseObject = { periodResponse: periodResponseData };
            if (availableSubUnits)
                responseObject['subUnitsResponse'] = availableSubUnits;
            
            return responseObject;
        },        
        // TODO: this could be a lot more elegant - almost the entire function is comprised of side-effects
        // in future, maybe have a separate phase for side-effects so it's "fetch, set, side-effects, render" or break this into a new action
        set: async (data) => {
            // Reset previously set fields
            setBookingState(resetMap.onPeriodChanged);

            const { 
                period, 
                departure_date,
                deposit_unformatted,
                booking_info,
                restrict_sub_unit_period,
                sub_units
            } = data.periodResponse;

            const { subUnitsResponse = null } = data;
            
            const bookingData = getBookingState();  
            const unitData = getUnitData(bookingData.unit);
                      
            let firstAvailableSubUnitId   = 0;
            let availableSubUnits         = [];
            let availablePitchTypes       = [];
            let useSubunitPitchTypeFilter = false;
            let usePetsFilter             = unitData.pets_flag == "1";
            
            const hideSubUnits = bookingData.hide_sub_units || (bookingData.restrict_sub_units && period < restrict_sub_unit_period);
            console.log(firstAvailableSubUnitId);
            if (subUnitsResponse?.data?.length > 0) {                             
                const clientData = getClientData();

                if (!clientData.sub_units) {
                    setClientDataProp('sub_units', sub_units);
                }

                const parentUnitsSubs = parseIntb10(unitData.multi_alloc_flag) == 1
                    ? clientData.sub_units.filter(subUnit => matchMultiParentUnitIds(subUnit, bookingData.unit))
                    : clientData.sub_units.filter(subUnit => matchSingleParentUnitId(subUnit, bookingData.unit));
                    
                // Create a set of available sub-unit IDs for quick lookup, there are often more than 100 sub-units   
                const availableIds = new Set(
                    subUnitsResponse.data.map(item => parseIntb10(item.sub_unit_id))
                );
                
                // Filter available sub-units
                availableSubUnits = parentUnitsSubs.filter(subUnit => {
                    const doesMatch = availableIds.has(parseIntb10(subUnit.id));
                    return doesMatch;
                });

                // Determine whether the pets filter should be active from the available sub-units.
                for (const subUnit of availableSubUnits) {
                    if (!usePetsFilter)
                        usePetsFilter = parseIntb10(subUnit.pets_flag) > 0 || parseIntb10(subUnit.pets_flag_seasonal) > 0;

                    if (usePetsFilter)
                        break;                    
                }

                const pitchTypeField = clientData.uiData.stage_1_field_config
                    ?.find(field => field.id === 'sub-unit-pitch-type');
                availablePitchTypes = getAvailablePitchTypes(
                    availableSubUnits,
                    pitchTypeField?.data || []
                );
                useSubunitPitchTypeFilter = shouldShowPitchTypeFilter(
                    unitData,
                    hideSubUnits,
                    availablePitchTypes
                );

                if (availableSubUnits.length > 0) {
                    firstAvailableSubUnitId = parseIntb10(availableSubUnits[0].id);
                }
            }

            const chosenPaymentType = booking_info.full_only ? 'full' : 'deposit';
            
            const newBookingState = {
                period,
                departure_date,
                deposit_unformatted,                
                restrict_sub_unit_period,
                av_sub_units       : availableSubUnits,
                av_pitch_types     : availablePitchTypes,
                using_pets_filter  : usePetsFilter,
                using_pitch_filter : useSubunitPitchTypeFilter,
                chosen_payment_type: chosenPaymentType
            }
            
            // A hidden pitch choice is an implicit "any pitch" selection. Persist it
            // before checkout can advance so the resulting booking is moveable in BMS.
            if (hideSubUnits && firstAvailableSubUnitId > 0) {
                newBookingState['sub_unit'] = firstAvailableSubUnitId;
                await runActionPhase('sub_unit', 'fetch', {
                    value: 'sub_unit', 
                    data: buildSubUnitSelectionRequest(firstAvailableSubUnitId, true)
                });
            }

            setBookingState(newBookingState);

            // Run the payment_select fetch action in the background
            // TODO: ideally, we'd have some sort of action pipeline/chain that could run actions that depend on other actions together synchronously - that would 
            // prevent the need to run actions like this inside of other actions
            runActionPhase(
                'payment_select', 
                'fetch', 
                { 
                    value: 'payment_type', 
                    data: { payment_type: chosenPaymentType, booking_number: 0 }
                }
            );

            setCostState(booking_info || {}); 
        },
        render: () =>UI.updateOnPeriodChange(getBookingState())
    },
    sub_unit: {
        fetch: setSession,
        set: (value, data) => {
            setBookingState(resetMap.onSubUnitChanged);
            setBookingStateProp('sub_unit', value);
            
            setCostState(data.booking_info);
        },
        render: () => { 
           UI.updateOnSubUnitChange(getBookingState(), getClientData().sub_units) 
        }
    },
    sub_unit_pitch_type: {
        fetch: (actionName, data) => setSession('pitch_type', {
            pitch_type: data.sub_unit_pitch_type
        }),
        set: (value, data) => {
            const selectedSubUnitId = parseIntb10(data?.sub_units?.[0]?.id) || null;

            setBookingState({
                sub_unit_pitch_type: parseIntb10(value),
                sub_unit: selectedSubUnitId
            });

            if (data?.booking_info)
                setCostState(data.booking_info);
        },
        render: () => {
            const bookingData = getBookingState();
            const selectedPitchType = bookingData.sub_unit_pitch_type;
            
            const filteredSubUnits = bookingData.av_sub_units.filter(su => su.pitch_type == selectedPitchType);
            showDropdownOptsInArray('sub-unit', filteredSubUnits);            
            
            const selectedSubUnit = filteredSubUnits.find(su => parseIntb10(su.id) == bookingData.sub_unit);
            if (selectedSubUnit) {
                setDropdown(elById('atb-sub-unit'), String(selectedSubUnit.id), false);
                updateSummarySubUnitUI(selectedSubUnit.name, false);
                updateSummary(bookingData.costs);
            } else {
                resetDropdown(elById('atb-sub-unit'), false);
                updateSummarySubUnitUI('', false);
            }
        }
    },
    filter_pet_friendly: {
        fetch: (actionName, data) => setSession('pets', { pets: data.filter_pet_friendly ? 1 : 0 }),
        set: (value, data) => {
            setBookingStateProp('pets_filter_active', value);

            if (value) {
                setBookingState({
                    sub_unit: null,
                    addons: {},
                    party_size: getBookingState().party_size || 1
                });
                return;
            }

            if (data?.booking_info) {
                setCostState(data.booking_info);
                setBookingStateProp('addons', data.booking_info.addons || {});
            }
        },
        render: (data) => {
            // TODO: Theoretically, pitch types/pet-friendly filters can be active at the same time. We're not accounting for that here
            //TODO: add pitch type filtering logic here too, will need to check if pitch type is active first
            const bookingState = getBookingState();

            updateSummary(bookingState.costs);
             
            if (bookingState?.is_single_unit)
                return;

            const isPetsFilterActive = bookingState.pets_filter_active;            
            let filteredSubUnits = getBookingState().av_sub_units;

            if (isPetsFilterActive) {
                const petFriendlyIds = new Set((data?.sub_units || []).map(su => parseIntb10(su.id)));
                filteredSubUnits = filteredSubUnits.filter(su => petFriendlyIds.has(parseIntb10(su.id)));
            }

            const hasNoVisibleSubUnits = !filteredSubUnits || filteredSubUnits.length === 0;

            if ((isPetsFilterActive && data && parseIntb10(data.no_pet_friendly_sub_units) === 1) || hasNoVisibleSubUnits) {
                showDropdownOptsInArray('sub-unit', []);
                setDropdownMessage(
                    elById('atb-sub-unit'),
                    isPetsFilterActive ? 'NO PET FRIENDLY UNITS AVAILABLE' : 'NO UNITS AVAILABLE'
                );
                toggle(elById('atb-sub-unit-row'), true, 'flex');
                toggle(elById('atb-sub-unit-pitch-type-row'), false);
                UI.updatePitchMapButton(getUnitData(bookingState.unit), []);
                setBookingStateProp('sub_unit', null);
                updateSummarySubUnitUI('', true);
                resetExtrasUI();
                return;
            }

            showDropdownOptsInArray(
                'sub-unit',
                filteredSubUnits
            );

            const firstAvSubunit = getFirstAvSubUnitFromArr(filteredSubUnits);
            const subUnitsAreHidden = bookingState.hide_sub_units || (bookingState.restrict_sub_units && bookingState.period < bookingState.restrict_sub_unit_period);
            const shouldAutoSelectSubUnit = !isPetsFilterActive || subUnitsAreHidden;

            toggle(elById('atb-sub-unit-row'), !subUnitsAreHidden, 'flex');
            UI.updatePitchMapButton(getUnitData(bookingState.unit), filteredSubUnits);

            if (shouldAutoSelectSubUnit) {
                if (firstAvSubunit) {
                    setDropdown(elById('atb-sub-unit'), String(firstAvSubunit.id));
                    updateSummarySubUnitUI(firstAvSubunit.name, !subUnitsAreHidden);
                }
            } else if (!bookingState.sub_unit) {
                resetDropdown(elById('atb-sub-unit'), false);
                updateSummarySubUnitUI('', true);
                resetExtrasUI();
            }
        }
    },
    party_size: {
        fetch: setSession,
        set: (value, data) => {
            setBookingStateProp('party_size', data.booking_info.party_size);
            setCostState(data.booking_info);
        },
        render: () => updateSummary(getBookingState().costs)
    },
    party: {
        fetch: (actionName, data) => { return setSession('party_members', data) },
        set: (value, data) => {
            // const availabilityType = getBookingStateProp('availability_type')
            
            // if (availabilityType == AvailabilityType.Occupancy) {
            //     // We need to find the number
            // }

            const partyMembers = getBookingState().party_members;
            const newPartyMembers = Object.fromEntries(
                Object.keys(partyMembers).map(key => [key, {
                    qty: 0, 
                    calc_include: partyMembers[key].calc_include
                }])
            );

            data.booking_info.party_members_breakdown.forEach(item => {
                const id = '_'+item.id;
                const qty = item.qty || 0;

                // If we already have this id, add the qty
                if (newPartyMembers[id] != null) newPartyMembers[id].qty += qty;
                else newPartyMembers[id].qty = qty;      
            });

            setBookingStateProp('party_members', newPartyMembers);
            setCostState(data.booking_info);
        },
        render: () => updateSummary(getBookingState().costs)        
    },
    addon: {
        fetch: setSession,
        set: (newAddon, data) => {            
            const addons = getBookingState().addons || {};

            const addonId = newAddon.addon_id;
            delete newAddon.addon_id;
            addons[addonId] = newAddon;

            setBookingStateProp('addons', addons);
            setCostState(data.booking_info);
        },
        render: () => updateSummary(getBookingState().costs)        
    },
    payment_select: createAction(
        'payment_select', {
            fetch: (value, data) => setSession('payment_type', data),
            set: (paymentTypeString, data) => {
                setBookingStateProp('chosen_payment_type', paymentTypeString);
                setCostState(data.booking_info);
            },
            render: () =>UI.updateOnPaymentChange(getBookingState())                         
        }
    ),
    set_party_details: createAction(
        'party_details', {
            fetch: setSession,
            set: (value, data) => {
                // atb-party-details-form
                // // setBookingState({
                // //     'party_details'
                // // })

            }
        }
    ),
    set_billing_details: createAction(
        'billing_details', {
            fetch: setSession,
            set: (value, data) => {
                // atb-party-details-form
                // // setBookingState({
                // //     'party_details'
                // // })
            }
        }
    ),
    add_booking: {
        fetch: (value, data) => bookingRequest('/checkout/startAnotherBooking/'),
        set: (value, data) => {
            // Retrieve the totalToPay and grand total from the previous booking's state
            initNewBooking({costs: transformCostData(data.booking_info)});                        
        },
        render: (data) => {                                  
            // Pass all of the booking data to create a new display data array for the multi-booking accordion
            const bookingDisplayArray = createBookingDisplayArray(getBookingStateArray(), getUnitData, getSiteNameForUnit);
            renderBookingAccordion(bookingDisplayArray, bookingDisplayArray.length - 1);

            updateSummary(getBookingState().costs);
            
            calendar.destroy({doHideWrapper: true});

            // Reset all of the drpodowns
            const stageOneFieldConfig = getClientData().uiData.stage_1_field_config;
            const showFields          = stageOneFieldConfig.filter(field => field.show_condition);


            let selectorString = showFields
                .map(field => `#atb-${field.id}`)
                .join(', ');
            selectorString += ', #atb-unit';

            const fieldsToReset = qsAll(selectorString);
            fieldsToReset.forEach(field => resetDropdown(field));

            const fieldsToHide = qsAll('#atb-sub-unit-row, #atb-sub-unit-pitch-type-row, #atb-party-size-row, #atb-filter-pet-friendly-row');
            fieldsToHide.forEach(el => toggle(el, false));

            toggle(elById('atb-unit-row'), true, 'flex');

            // Hide party member section
            toggle(elById('atb-party-members-section'), false);

            // Wipe extras and answers from the previous booking
            resetExtrasUI(false, true);

            // Return to the first stage specified in the config, once everything has been reset
            returnToStageUI(getCurrentStageId());            

            // Clean calendar button event listeners to prevent a possible preset one-off event firing
            const calendarBtnEl = elById('atb-calendar-btn');
            const cleanBtn      = calendarBtnEl.cloneNode(true);
            calendarBtnEl.replaceWith(cleanBtn);            

            // Hide gdpr and referrals - users who have already completed don't need to interact with either again
            qsAll('#atb-gdpr-row, #atb-referral-dropdown-row')
                .forEach(el => toggle(el, false));

            // TODO: should probably not be here but will live with it for now
            toggle(elById('atb-summary-booking-total'), true);

            toggle(elById('atb-multi-booking-payment-readout'), true);
            toggle(elById('atb-single-booking-payment-readout'), false);
        }
    },

    delete_booking: {
        fetch: (value, bookingIndex) => bookingRequest('/checkout/removeBooking/', bookingIndex + 1),
        set: (bookingIndex, data) => {            
            deleteBookingAtIndex(parseIntb10(bookingIndex));
        },
        render: () => {                      
            
            const bookingDisplayArray = createBookingDisplayArray(
                getBookingStateArray(),
                getUnitData,
                getSiteNameForUnit
            );

            const bookingData = getBookingState();

            // TODO: will also have to set-up the calendar again
            let elementsToUpdate = [
                { id: 'category', type: 'dropdown', value: bookingData.category },
                { id: 'unit',     type: 'dropdown', value: bookingData.unit },
                { id: 'calendar', type: null, value: null }
            ];

            if (bookingData.site > 0)
                elementsToUpdate.unshift({ id: 'site', type: 'dropdown', value: bookingData.site });

            if (!bookingData.is_single_unit && !bookingData.hide_sub_units)
                elementsToUpdate.push({ id: 'sub-unit', type: 'dropdown', value: bookingData.sub_unit });

            if (bookingData.using_pets_filter)
                elementsToUpdate.push({ id: 'filter-pet-friendly', type: 'checkbox', value: bookingData.pets_filter_active });                

            if (bookingData.using_pitch_filter)
                elementsToUpdate.push({ id: 'sub-unit-pitch-type', type: 'dropdown', value: null });
            
            if (bookingData.using_party_size)
                elementsToUpdate.push({ id: 'party-size', type: 'dropdown', value: bookingData.costs.party_size });            

            elementsToUpdate.forEach(element => {
                const el = qs(`#atb-${element.id}-row`);

                if (element.value) {                    
                    switch(element.type) {
                        case 'checkbox': {
                            const input = el.querySelector('input');
                            input.value = element.value;
                            break;
                        }
                        case 'dropdown': {
                            const input = el.querySelector(`input#atb-${element.id}`);
                            
                            setDropdown(input, typeof element.value == 'string' ? element.value : element.value.toString(), false);
                            break;
                        }
                    }
                }

                toggle(el, true, element.id == 'calendar' ? 'block' : 'flex');
            });            

            calendar.destroy({doHideWrapper: false});
            // Update the calendar button text with this bookings arrival and departure dates
            calendar.updateBtnText(bookingData.arrival_date, bookingData.departure_date, bookingData.period);
            
            // TODO: change the way drawer ids work, '2' tells me nothing about which drawer component is opening 
            toggleInputDrawer('calendar', false);
            
            const partyMemberSteppers = filterElementsByParentId(qsAll('.atb-party.atb-stepper'), bookingData.unit);
            
            // Show party members for the unit attached to this booking, and initialise them witht he values from the booking
            showOptionsWithParentId('#atb-party-members-section', '.atb-stepper', bookingData.unit);
            initialisePartyMemberSteppers(partyMemberSteppers, bookingData.party_members, false);

            // Show extras for the unit attached to this booking
            resetExtrasUI(false, true);

            // Set the extras to the values from the booking - happens when user deletes current booking
            if (!isEmpty(bookingData.addons))
                setExtrasUI(bookingData.addons);

            // Build Multi booking summary
            renderBookingAccordion(bookingDisplayArray, bookingDisplayArray.length - 1);
            updateSummary(getBookingState().costs);

            // Re-enable both rows if the user has dropped all but the first booking
            if (bookingDisplayArray.length < 2) {
                qsAll('#atb-gdpr-row, #atb-referral-dropdown-row')
                    .forEach(el => toggle(el, true));

                // TODO: should move this into the checkout-summary
                toggle(elById('atb-summary-booking-total'), false);

                toggle(elById('atb-multi-booking-payment-readout'), false);
                toggle(elById('atb-single-booking-payment-readout'), true);
            }
        }
    },
    preset_booking: {
        set: (stageOneConfig, presetBookingObj) => {

            const unitData   = presetBookingObj.unit;
            const unitId     = unitData.id;            

            // refactor
            const stageOneById = stageOneConfig.reduce((acc, field) => {
                acc[field.id] = field;
                return acc;
            }, {});
        
            const units        = stageOneById['unit']?.data;
            const subUnits     = stageOneById['sub-unit']?.data || [];
            const partyMembers = stageOneById['party-members']?.data;
            const pitchFilter  = stageOneById['sub-unit-pitch-type'] || {};
            const petsFilter   = stageOneById['filter-pet-friendly'] || {};
            setClientDataProp('units', units);
            setClientDataProp('sub_units', subUnits);
            setClientDataProp('party_members', partyMembers);

            const bookingQuantitiesById = presetBookingObj.party_members.reduce(
                (acc, member) => {
                    if (!member || typeof member.id !== 'number') return acc;
                    acc[member.id] = (acc[member.id] || 0) + 1;
                    return acc;
                }, {}
            );
            const hasPresetPartyMemberQuantities = Object.keys(bookingQuantitiesById).length > 0;

            const partyMembersForUnit = buildInitialPartyMembersForUnit(
                partyMembers,
                unitId,
                hasPresetPartyMemberQuantities ? bookingQuantitiesById : null
            );
            

            // TODO: doing a full assignment here but also assigning properties that I know need changing below, may be better to do individual property assignments
            // once we know exactly what we need
            setBookingState(presetBookingObj);

            const thumbnail = getUnitThumbnailSrcById(unitData.id);
            // Patch unit data
            patchUnitDataById(
                unitData.id, 
                {
                    availabilityType: parseIntb10(unitData.availability_type),
                    address: buildUnitAddressString(unitData.address.city, unitData.address.state), 
                    thumbnail: thumbnail
                }
            );
            
            const availableIds = new Set(
                presetBookingObj.av_sub_units?.map(item => parseIntb10(item.sub_unit_id))
            );

            // Filter available sub-units
            const availableSubUnits = subUnits?.filter(subUnit =>
                availableIds.has(parseIntb10(subUnit.id))
            );
            
            const isSingleUnit     = parseIntb10(unitData.single_plot) == 1;
            const doesRestrictSubs = parseIntb10(unitData.restrict_sub_units) == 1;
            const restrictPeriod   = parseIntb10(presetBookingObj.restrict_sub_unit_period);
            const doesHideSubs     = parseIntb10(unitData.hide_sub_units) == 1;

            const subUnitsAreHidden = doesHideSubs || (doesRestrictSubs && presetBookingObj.period < restrictPeriod);
            const availablePitchTypes = getAvailablePitchTypes(
                availableSubUnits,
                pitchFilter.data || []
            );
            const usePitchFilter = shouldShowPitchTypeFilter(
                unitData,
                subUnitsAreHidden,
                availablePitchTypes
            );

            const availabilityType      = parseIntb10(unitData.availability_type);
            const usePartySizeDropdown = shouldUsePartySizeDropdown(partyMembersForUnit, availabilityType);
            const initialPartySize      = getInitialPartySize({
                availabilityType,
                usePartySizeDropdown,
                partyMembersForUnit,
                stageOneFieldConfig: stageOneConfig,
                presetPartySize: presetBookingObj.preset_party_size
            });

            setBookingState({
                // basics
                unit    : unitId,
                sub_unit: parseIntb10(presetBookingObj.sub_unit?.sub_unit_id) ?? null,

                    // filters 
                using_party_size        : usePartySizeDropdown,
                using_pitch_filter      : !isSingleUnit && usePitchFilter,
                using_pets_filter       : petsFilter.show_condition == 1,
                
                    // available sub units if applicable
                av_sub_units            : availableSubUnits,
                av_pitch_types          : availablePitchTypes,
                availability_type       : availabilityType,
                restrict_sub_unit_period: restrictPeriod,
                restrict_sub_units      : doesRestrictSubs,
                hide_sub_units          : doesHideSubs,
                is_single_unit          : isSingleUnit,
                party_members           : partyMembersForUnit,
                party_size              : initialPartySize
            });

            // TODO: find a nicer way of doing this
            unitData['thumbnail'] = thumbnail;

            // TODO: this data all comes from wildly different places right now - perhaps simplify the payload in the PHP stack
            presetBookingObj.costs['party_size'] = initialPartySize;

            presetBookingObj.costs['full_only'] = isUsingFullPaymentOnly(
                presetBookingObj.costs['full_only'], 
                presetBookingObj.costs['deposit_unformatted'],
                presetBookingObj.costs['total_unformatted']
            );

            setCostState(presetBookingObj.costs);

            UI.initPresetUI(
                getBookingState(), 
                getClientData(), 
                unitData, 
                presetBookingObj.terms
            );
        }
    },

    // TODO: May want to create this action from inside of the XCover module on "init" using "addAction"
    // that would mean it would only exist if the module is actually in use
    xcover: createAction(
        'xcover',
        {
            fetch: (endpoint, data) => setSession('custom_addon', data),
            set: (value, data) => {
                const { addon_value: useBookingProtect } = value;
                
                setBookingStateProp("has_run_refund_quote", true);
                setBookingStateProp("use_booking_protect", useBookingProtect == 1);
                setCostState(data.booking_info);
            },
            render: () => {
                const bookingData = getBookingState();
                const optionEl    = qs(`.atb-xcover-extra-wrapper input[type="radio"][data-value="${bookingData.use_booking_protect ? 1 : 0}"]`);

                // Move the selected class to the other option
                qs('.atb-xcover-extra-wrapper input[type="radio"].selected')?.classList.remove('selected');    
                optionEl.classList.add('selected');    
                optionEl.checked = true;

                updateSummary(bookingData.costs);
            }
        }
    ),
    discount_code: createAction(
        'discount_code',
        {
            fetch: (value, data) => setSession('discount_code', data),

            set: (discountString, data) => {
                if (data.discount_ok) {
                    setCostState(data.booking_info);
                }                
            },

            render: () => {
                const bookingData = getBookingState();                
                updateSummary(bookingData.costs);
            }
        }
    ),
    remove_discount_code: createAction(
        'remove_discount_code',
        {
            fetch: (value, data) => {
                return setSession('discount_code', { discount_code: '' });
            },

            set: (discountString, data) => {
                setCostState(data.booking_info);
            },

            render: () => {
                updateSummary(getBookingState().costs);
            }
        }
    )
}


// Attach callbacks to the "arrival date" and "departure date" selected handler
calendar.onStartSelected((arrivalDate) => {
    const bookingData = getBookingState();
    const unitData    = getUnitData(bookingData.unit);

    if (parseIntb10(unitData?.enquiry_form) === 1) {
        const enquiryArrivalDate = toIsoDateString(arrivalDate);
        if (!enquiryArrivalDate) {
            console.error('Unable to redirect to the enquiry form: invalid arrival date', arrivalDate);
            return;
        }

        window.location = `/enquiry/placeEnquiry/${bookingData.category}/${bookingData.unit}/${enquiryArrivalDate}/1/`;
        return;
    }

    setBookingStateProp('arrival_date', arrivalDate);
    setBookingStateProp('period', 0);
});

calendar.onEndSelected(async(period) => {
    const data = await actions['period'].fetch('period', { period });
    data['period'] = period;
    await actions['period'].set(data);
    actions['period'].render();
});


/**
 * An object representing the values that need to be reset when moving back to certain steps in the booking process
 */
const resetMap = {
    onSiteChange: {
        category: null,
        unit: null,
        using_party_size: false,
        party_members: [],
        is_single_unit: null,
        hide_sub_units: null,
        restrict_sub_units: null,
        restrict_sub_unit_period: null,
        costs: {},
        av_sub_units: null,
        av_pitch_types: [],
        pets_filter_active: false,
        using_pets_filter: false,
        sub_unit_pitch_type: null,
        using_pitch_filter: false,
        period: 0,
        party_size: 0,
        availability_type: null
    },
    onCategoryChange: {
        unit: null,
        using_party_size: false,
        party_members: [],
        is_single_unit: null,
        hide_sub_units: null,
        restrict_sub_units: null,
        restrict_sub_unit_period: null,
        costs: {},
        av_sub_units: null,
        av_pitch_types: [],
        pets_filter_active: false,
        using_pets_filter: false,
        sub_unit_pitch_type: null,
        using_pitch_filter: false,
        period: 0,
        party_size: 0,
        availability_type: null
    },
    onUnitChange: {
        using_party_size: false,
        party_members: [],
        is_single_unit: null,
        hide_sub_units: null,
        restrict_sub_units: null,
        restrict_sub_unit_period: null,
        costs: {},
        av_sub_units: null,
        av_pitch_types: [],
        pets_filter_active: false,
        using_pets_filter: false,
        sub_unit_pitch_type: null,
        using_pitch_filter: false,
        period: 0,
        party_size: 0,
        addons: {}
    },
    onPeriodChanged: {
        costs: {},
        av_sub_units: null,
        av_pitch_types: [],
        pets_filter_active: false,
        using_pets_filter: false,
        sub_unit_pitch_type: null,
        using_pitch_filter: false
    },
    onSubUnitChanged: {
        party_size: 0, 
    }
}
